import {
    isInternalRoute,
    isStudioAIRoute,
    RouteAccessParams,
    shouldDisableRoute,
} from '@/@studio/utils';
import { NAV_SECTIONS, NavChild } from '../constants';

/**
 * The four access governors, expressed as plain data.
 *
 * This is the whole gating vocabulary of the app's two IA surfaces — the
 * left-hand nav (`@nav`) and the Home screen's stage tiles (`@home`). Both
 * resolve through `resolveAccess` below, so a capability cannot be enabled on
 * one surface and disabled on the other. Before this module they each had
 * their own resolution: the nav applied all four, Home applied only
 * `internalHref`, and the two drifted far enough apart to block a release.
 */
export interface AccessGate {
    href?: string;
    /** Real destination for internal (@mfour.com) viewers only. Everyone else
     * is disabled, whatever `href`/`comingSoon` say. Load-bearing for any
     * destination that route access alone does not cover — see the
     * Feasibility note in `NAV_GATES_BY_NAME`. */
    internalHref?: string;
    /** `internalHref` opens only for an internal viewer who also holds this
     * flag. Every other viewer, staff included, gets the inert row a client
     * gets. For an internal alpha with named testers (Slide Creator,
     * `slide-creator-alpha`). */
    internalRequiresFlag?: string;
    /** No destination exists yet for anyone. */
    comingSoon?: boolean;
    /** Hidden entirely for anyone but internal (@mfour.com) viewers — not greyed out, not
     * listed. For an internal-only section such as Analyze › Data Prep. */
    internalOnly?: boolean;
    /** Hidden in the nav / disabled on Home unless this flag is on. */
    requiresFlag?: string;
    /** Ask DANI specifically: per-user or org-trial access, not a flag. */
    requiresDaniAccess?: boolean;
    /** Destination is Ask DANI via a seed prompt, so it route-checks against
     * `/dani` even though the gate carries no `href` of its own. */
    seedsDani?: boolean;
    /** Destination is a flyout, action list or modal rather than an href —
     * enough to count as "has somewhere to go" for `no-destination`. */
    fansOut?: boolean;
}

export interface AccessContext {
    /** `effectiveOrg.isInternalForTiles` — false while "View as" is active. */
    isInternal: boolean;
    daniVisible: boolean;
    hasFlag: (key: string) => boolean;
    routeAccessParams: RouteAccessParams;
    /** `effectiveOrg.orgName` — the viewer's own org (or the org being
     * simulated under View-as). Only consulted by `requiresOrgNameMatch`
     * fan-out items today; every other gate stays org-name-agnostic. */
    orgName: string;
}

export type AccessReason =
    // Entitlement — the org was never granted this.
    | 'flag'
    | 'dani'
    // Reachability — the capability exists but not for this viewer, now.
    | 'coming-soon'
    | 'internal-only'
    | 'no-destination'
    | 'no-license'
    | 'no-dani-access';

export interface AccessVerdict {
    /** The nav drops these rows. Home renders them disabled instead — a
     * deliberate per-surface difference in presentation, not in the decision
     * (Home advertises capability the org has not bought; the nav stays
     * clean). Always accompanied by `isDisabled`, so a consumer that ignores
     * `isHidden` still fails closed. */
    isHidden: boolean;
    isDisabled: boolean;
    /** The destination for THIS viewer — `undefined` when they have none.
     * Never the derived `/dani` of a `seedsDani` gate, so a seed tile still
     * seeds rather than navigating. */
    href?: string;
    reason?: AccessReason;
}

/** Hover copy for a disabled item, shared by both surfaces so a license-blocked
 * tile and its nav row cannot read differently. */
export const DISABLED_TOOLTIP: Record<AccessReason, string> = {
    flag: 'Coming soon',
    dani: 'Coming soon',
    'coming-soon': 'Coming soon',
    'internal-only': 'Coming soon',
    'no-destination': 'Coming soon',
    'no-license': 'Requires an active license',
    'no-dani-access': 'Requires Ask DANI access',
};

/** Whether a disabled item prints the small "Coming soon" caption under its
 * label. Only true where nothing exists to reach yet — a tile greyed out
 * because a license lapsed is not "coming soon", and saying so is a lie. */
export function showsComingSoonCaption(reason?: AccessReason): boolean {
    return reason === 'coming-soon' || reason === 'internal-only' || reason === 'no-destination';
}

/** Whether a gate's `internalHref` opens for this viewer. The nav's own
 * inert styling asks this too, so a row cannot look live and resolve
 * disabled. */
export function opensInternalHref(
    gate: Pick<AccessGate, 'internalRequiresFlag'>,
    ctx: Pick<AccessContext, 'isInternal' | 'hasFlag'>,
): boolean {
    if (!ctx.isInternal) return false;
    return !gate.internalRequiresFlag || ctx.hasFlag(gate.internalRequiresFlag);
}

/**
 * Resolves one capability against one viewer.
 *
 * Governor order is a deliberate port of the left nav's own resolution
 * (`use-nav-sections.ts`) and must stay in this order: `requiresDaniAccess`
 * short-circuits before `requiresFlag` is even consulted, `internalHref`
 * overrides `comingSoon`, and `comingSoon` beats route access. No item uses
 * both entitlement governors today; the port keeps the precedence anyway so
 * adding one later cannot silently change meaning.
 */
export function resolveAccess(gate: AccessGate, ctx: AccessContext): AccessVerdict {
    if (gate.internalOnly && !ctx.isInternal) {
        return { isHidden: true, isDisabled: true, reason: 'internal-only' };
    }
    if (gate.requiresDaniAccess) {
        if (!ctx.daniVisible) return { isHidden: true, isDisabled: true, reason: 'dani' };
    } else if (gate.requiresFlag) {
        if (!ctx.hasFlag(gate.requiresFlag)) {
            return { isHidden: true, isDisabled: true, reason: 'flag' };
        }
    }

    // `internalHref` replaces the destination rather than short-circuiting, so
    // an internal viewer's own href still faces the route check below — the
    // same thing the nav does today.
    let href = gate.href;
    let comingSoon = gate.comingSoon;
    if (gate.internalHref) {
        if (!opensInternalHref(gate, ctx)) {
            return { isHidden: false, isDisabled: true, reason: 'internal-only' };
        }
        href = gate.internalHref;
        comingSoon = false;
    }

    if (comingSoon) {
        return { isHidden: false, isDisabled: true, href, reason: 'coming-soon' };
    }

    // A seed-DANI item has no href of its own but still lands on /dani, so it
    // has to face the same route check as a tile that links there directly.
    const effectiveHref = href ?? (gate.seedsDani ? '/dani' : undefined);

    if (effectiveHref && shouldDisableRoute(effectiveHref, ctx.routeAccessParams)) {
        return {
            isHidden: false,
            isDisabled: true,
            // The href is still returned: the nav's `isSelected` check matches
            // the current path against it even while the row is disabled.
            href,
            reason: isStudioAIRoute(effectiveHref)
                ? 'no-dani-access'
                : isInternalRoute(effectiveHref)
                  ? 'internal-only'
                  : 'no-license',
        };
    }

    if (!effectiveHref && !gate.fansOut) {
        return { isHidden: false, isDisabled: true, reason: 'no-destination' };
    }

    return { isHidden: false, isDisabled: false, href };
}

/** Names that appear on more than one nav child. Must stay empty — a duplicate
 * would let a Home tile inherit the wrong capability's gate. Asserted by
 * `home-nav-parity.test.ts` rather than thrown at module load, so a bad edit
 * fails the suite instead of the whole app. */
export const NAV_GATE_DUPLICATE_NAMES: string[] = [];

/**
 * Every nav child's gate, keyed by its display name.
 *
 * `NAV_SECTIONS` is the single declaration site for what is gated and how.
 * Home tiles carry no gating of their own; they look themselves up here by
 * name, which is why the two surfaces cannot drift. Name is the join key
 * rather than href because it is the visible label — renaming one is a
 * deliberate, reviewed act, and `home-nav-parity.test.ts` asserts the hrefs
 * agree anyway.
 *
 * Note that two gates that look alike are not alike: `/feasibility` sits in
 * `INTERNAL_ROUTES`, so route access alone would gate Feasibility. A
 * licensed `/portal/...` path would not — that is why `internalHref` still
 * exists for destinations that must stay internal even when the URL itself
 * is an ordinary licensed route.
 */
export const NAV_GATES_BY_NAME: ReadonlyMap<string, AccessGate> = (() => {
    const map = new Map<string, AccessGate>();
    for (const child of NAV_SECTIONS.flatMap((section) => section.children)) {
        if (map.has(child.name)) {
            NAV_GATE_DUPLICATE_NAMES.push(child.name);
            continue;
        }
        map.set(child.name, gateFromNavChild(child));
    }
    return map;
})();

export function navGateFor(name: string): AccessGate | undefined {
    return NAV_GATES_BY_NAME.get(name);
}

/** One entry in a fan-out list — a nav flyout row or a Home tile action. */
export interface FanOutItem {
    href?: string;
    requiresFlag?: string;
    /**
     * Only kept for internal viewers, or external viewers whose own org name
     * contains this string (case-insensitive) — e.g. a client-specific
     * vTracker dashboard that isn't gated by a real flag or license, just by
     * which org it belongs to. A name-contains check rather than an exact
     * org id match: no verified Salesforce Account ID was available for
     * these orgs at the time this was added, so treat this as an interim
     * heuristic — replace with an id-based check if one becomes available.
     */
    requiresOrgNameMatch?: string;
}

/**
 * The reachable subset of a fan-out list.
 *
 * Flyout rows and tile actions carry their own destinations, so a flag is not
 * the only thing that can put one out of reach — an expired license blocks the
 * ungated ones too. Both surfaces filter through here so a dashboard cannot be
 * listed in one place and withheld in the other.
 */
export function filterFanOut<T extends FanOutItem>(
    items: T[] | undefined,
    ctx: AccessContext,
): { items: T[] | undefined; routeBlockedAny: boolean } {
    if (!items) return { items: undefined, routeBlockedAny: false };

    let routeBlockedAny = false;
    const kept = items.filter((item) => {
        if (item.requiresFlag && !ctx.hasFlag(item.requiresFlag)) return false;
        if (
            item.requiresOrgNameMatch &&
            !ctx.isInternal &&
            !ctx.orgName?.toLowerCase().includes(item.requiresOrgNameMatch.toLowerCase())
        ) {
            return false;
        }
        if (item.href && shouldDisableRoute(item.href, ctx.routeAccessParams)) {
            routeBlockedAny = true;
            return false;
        }
        return true;
    });
    return { items: kept, routeBlockedAny };
}

/**
 * A capability that fans out is only as reachable as the things it fans out
 * to. Once they have all been filtered away it has nowhere to go, so it turns
 * disabled rather than staying a live control that opens an empty list.
 */
export function applyFanOut(
    verdict: AccessVerdict,
    declaredItemCount: number,
    survivingItemCount: number,
    routeBlockedAny: boolean,
): AccessVerdict {
    if (verdict.isDisabled || declaredItemCount === 0) return verdict;
    if (survivingItemCount > 0 || verdict.href) return verdict;

    return {
        ...verdict,
        isDisabled: true,
        reason: routeBlockedAny ? 'no-license' : 'flag',
    };
}

/**
 * A row that has a destination of its own offers its fan-out only as a
 * choice between places. Once fewer than two survive the filtering above there
 * is nothing to choose, so the fan-out is dropped and the row stays a plain
 * link. Feasibility is the case: its DANI row needs `feasibility-dani`, and
 * without that flag the legacy tool is the only place to go. A row with no
 * destination of its own (Dashboards) keeps whatever survives.
 */
/**
 * The surviving choice marked `primary`, when there is still a choice to make:
 * the row or tile itself then goes there instead of to its own href. A choice
 * filtered out (its flag off) never wins, so the own href stays the fallback.
 */
export function primaryChoiceHref(
    ownHref: string | undefined,
    items: { href?: string; primary?: boolean }[] | undefined,
): string | undefined {
    if (!ownHref || !items || items.length < 2) return ownHref;
    return items.find((i) => i.primary && i.href)?.href ?? ownHref;
}

export function choiceOrNothing<T>(
    ownHref: string | undefined,
    items: T[] | undefined,
): T[] | undefined {
    if (ownHref && items && items.length < 2) return undefined;
    return items;
}

/**
 * A nav child's own gate.
 *
 * Deliberately never sets `seedsDani`: no nav row seeds a DANI prompt today,
 * and inferring one for the Synthesize-channels modal would make that row
 * inert for viewers who can see it now. The nav is the surface QA signed off,
 * so this refactor changes none of its verdicts — see
 * `nav-access-characterization.test.ts`.
 */
export function gateFromNavChild(child: NavChild): AccessGate {
    return {
        href: child.href,
        internalHref: child.internalHref,
        internalRequiresFlag: child.internalRequiresFlag,
        comingSoon: child.comingSoon,
        internalOnly: child.internalOnly,
        requiresFlag: child.requiresFlag,
        requiresDaniAccess: child.requiresDaniAccess,
        fansOut: !!child.flyout?.length || !!child.opensSynthesizeModal,
    };
}

/**
 * One nav row, fully resolved — gate plus fan-out. This is the verdict the
 * Home screen must agree with, so `home-nav-parity.test.ts` compares against
 * this rather than against `resolveAccess` alone.
 */
export function resolveNavChild(
    child: NavChild,
    ctx: AccessContext,
): AccessVerdict & { flyout?: NavChild['flyout'] } {
    const verdict = resolveAccess(gateFromNavChild(child), ctx);
    const { items, routeBlockedAny } = filterFanOut(child.flyout, ctx);
    const resolved = applyFanOut(
        verdict,
        child.flyout?.length ?? 0,
        items?.length ?? 0,
        routeBlockedAny,
    );
    return {
        ...resolved,
        href: primaryChoiceHref(resolved.href, items),
        flyout: choiceOrNothing(resolved.href, items),
    };
}
